import {
  Box,
  Button,
  Flex,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  FormControl,
  FormErrorMessage,
  Input,
} from '@chakra-ui/react';
import InputField from 'components/fields/InputField';
import React, { useState } from 'react';
import { Vendor, vendorRepository } from 'api/vendorRepository';
import { z } from 'zod';
import { vendorSchema, flattenZodErrors } from 'validations/Validations';
import { useAuth } from "contexts/authContext";

interface EditVendorFormProps {
  vendor: Vendor;
  onClose?: () => void;
  onUpdate?: (vendor: Vendor) => void;
  showCenteredAlert: (message: string, status: 'success' | 'error' | 'warning') => void;
}

export default function EditVendorForm({
  vendor,
  onClose,
  onUpdate,
  showCenteredAlert,
}: EditVendorFormProps) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [isUpdating, setIsUpdating] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [vendorData, setVendorData] = useState({
    name: vendor.name || '',
    contactEmail: vendor.contactEmail || '',
    contactNumber: vendor.contactNumber || '',
    organization: vendor.organization || '',
    status: vendor.status || 'Active',
    gsTin: vendor.gsTin || '',
    address: {
      locationName: vendor.address?.locationName || '',
      street: vendor.address?.street || '',
      city: vendor.address?.city || '',
      state: vendor.address?.state || '',
      zipCode: vendor.address?.zipCode || '',
      country: vendor.address?.country || '',
    },
  });

  const { role } = useAuth();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    (vendor as any)?.logoUrl || null
  );
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setVendorData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const fieldSchema = vendorSchema.shape[name as keyof typeof vendorData];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [name]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVendorData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));

    const fieldSchema = vendorSchema.shape.address.shape[name as keyof typeof vendorData.address];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [`address.${name}`]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleUpdate = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      setErrors({});
      vendorSchema.parse(vendorData);

      const payload: Vendor = {
        name: vendorData.name,
        contactEmail: vendorData.contactEmail,
        contactNumber: vendorData.contactNumber,
        organization: vendorData.organization,
        status: vendorData.status || 'Active',
        gsTin: vendorData.gsTin,
        address: {
          locationName: vendorData.address.locationName,
          street: vendorData.address.street,
          city: vendorData.address.city,
          state: vendorData.address.state,
          zipCode: vendorData.address.zipCode,
          country: vendorData.address.country,
        },

        logoFile: logoFile || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      await vendorRepository.patchVendor(vendor.vendorId!, payload);
      const refreshedVendor = await vendorRepository.getVendorById(vendor.vendorId!);
      if (onUpdate && refreshedVendor) onUpdate(refreshedVendor);

      showCenteredAlert('Vendor updated successfully!', 'success');
      if (onClose) onClose();
    } catch (error: unknown) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        // showCenteredAlert('Please fix the highlighted errors.', 'warning');
      } else {
        showCenteredAlert('Failed to update vendor.', 'error');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
   
      <Flex direction="column" w="100%" padding={4} borderRadius="16px">
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
          {/* Left Column */}
          <Stack direction="column" gap="20px">
            <FormControl isInvalid={!!errors['name']}>
              <InputField
                name="name"
                placeholder="Vendor Name"
                label="Vendor Name"
                value={vendorData.name}
                onChange={handleInputChange}
              />
              <FormErrorMessage>{errors['name']}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors['contactNumber']}>
              <InputField
                name="contactNumber"
                placeholder="Contact Number"
                label="Contact Number"
                value={vendorData.contactNumber}
                onChange={handleInputChange}
              />
              <FormErrorMessage>{errors['contactNumber']}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors['gsTin']}>
              <InputField
                name="gsTin"
                placeholder="GSTIN"
                label="GSTIN"
                value={vendorData.gsTin}
                onChange={handleInputChange}
                readOnly
              />
              <FormErrorMessage>{errors['gsTin']}</FormErrorMessage>
            </FormControl>
          </Stack>

          {/* Right Column */}
          <Stack direction="column" gap="20px">
            <FormControl isInvalid={!!errors['contactEmail']}>
              <InputField
                name="contactEmail"
                placeholder="Contact Email"
                label="Contact Email"
                value={vendorData.contactEmail}
                onChange={handleInputChange}
              />
              <FormErrorMessage>{errors['contactEmail']}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors['status']}>
              <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                Status
              </Text>
              <Select
                name="status"
                placeholder="Select status"
                value={vendorData.status}
                onChange={handleInputChange}
                bg={inputBg}
                color={vendorData.status ? textColor : placeholderColor}
              >
                <option
                  value="Active"
                  style={{ backgroundColor: menuListBg, color: textColor }}
                >
                  Active
                </option>
                <option
                  value="Inactive"
                  style={{ backgroundColor: menuListBg, color: textColor }}
                >
                  Inactive
                </option>
              </Select>
              <FormErrorMessage>{errors['status']}</FormErrorMessage>
            </FormControl>
          </Stack>
          {role === "Vendor" && (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" gridColumn={{ base: 'span 1', md: 'span 2' }}>

              {/*  LOGO */}
              <FormControl>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Vendor Logo
                </Text>

                {/* Preview */}
                {previewUrl && (
                  <Box mb={3}>
                    <Box
                      as="img"
                      src={previewUrl}
                      alt="Vendor Logo"
                      boxSize="110px"
                      objectFit="cover"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor="gray.200"
                      _dark={{ borderColor: "whiteAlpha.300" }}
                    />
                  </Box>
                )}

                {/* File Input */}
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      setLogoFile(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />
              </FormControl>

              {/*  ATTACHMENTS */}
              <FormControl>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Vendor Documents
                </Text>

                {/* Existing files (if any) */}
                {vendor.attachments && vendor.attachments.length > 0 && (
                  <Box mb={2}>
                    {vendor.attachments
                      .filter((a): a is string => typeof a === 'string')
                      .map((url, index) => (
                        <Text key={index} fontSize="sm" color="blue.500">
                          Document {index + 1}
                        </Text>
                      ))}
                  </Box>
                )}

                {/* Upload */}
                <Input
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      const files = Array.from(e.target.files);
                      setAttachments(files);
                    }
                  }}
                />

                {/* Selected files preview */}
                {attachments.length > 0 && (
                  <Box mt={2}>
                    {attachments.map((file, index) => (
                      <Text key={index} fontSize="sm">
                        {file.name}
                      </Text>
                    ))}
                  </Box>
                )}
              </FormControl>

            </SimpleGrid>
          )}

          {/* Address Section */}
          <Text
            color={textColor}
            fontSize="lg"
            fontWeight="600"
            mb="10px"
            gridColumn={{ base: 'span 1', md: 'span 2' }}
          >
            Address Details
          </Text>

          {['locationName', 'street', 'city', 'state', 'zipCode', 'country'].map((field) => (
            <FormControl key={field} isInvalid={!!errors[`address.${field}`]}>
              <InputField
                name={field}
                placeholder={field}
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={vendorData.address[field as keyof typeof vendorData.address]}
                onChange={handleAddressChange}
              />
              <FormErrorMessage>{errors[`address.${field}`]}</FormErrorMessage>
            </FormControl>
          ))}
        </SimpleGrid>

        {/* Buttons */}
        <Flex justify="center" gap="20px" mt="24px">
          {(role === "Admin" || role === "OrgAdmin" || role === "Vendor") && (
            <Button variant="darkBrand" onClick={handleUpdate} isDisabled={isUpdating} isLoading={isUpdating}>
              Update
            </Button>
          )}
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
        </Flex>
      </Flex>
    // </Card>
  );
}
